import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../context/ApiProvider';
import {
  type CandidateApplicationRecord,
  type CandidateApplicationStatus,
  type CareerJob,
  type JobApplicationPhase,
  type JobListingStatus,
  createDefaultJobApplicationPhases,
  getCareerJobApplicationPhases,
  getCareerJobListingStatus,
  normalizeCandidateApplicationStatus,
} from '../lib/careers/registry';

const ADMIN_JOBS_LIST_ENDPOINTS = ['/admin/jobs', '/admin/careers/jobs'] as const;
const ADMIN_JOB_UPDATE_ENDPOINTS = ['/admin/jobs', '/admin/careers/jobs'] as const;
const ADMIN_CANDIDATE_LIST_ENDPOINTS = [
  '/admin/candidates',
  '/admin/job-candidates',
  '/admin/careers/candidates',
] as const;
const ADMIN_CANDIDATE_UPDATE_ENDPOINTS = ['/admin/candidates', '/admin/job-candidates'] as const;

function asText(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function asTextArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => asText(item))
      .filter((item): item is string => Boolean(item));
  }
  const text = asText(value);
  if (!text) return [];
  return text
    .split(/\r?\n|;/g)
    .map((item) => item.trim())
    .filter(Boolean);
}

function coerceWorkType(value: unknown): CareerJob['workType'] {
  const text = asText(value)?.toLowerCase() ?? '';
  if (text.includes('part')) return 'part_time';
  if (text.includes('contract')) return 'contract';
  return 'full_time';
}

function coerceListingStatus(
  value: unknown,
  publishedFallback: boolean,
): JobListingStatus {
  const text = asText(value)?.toLowerCase() ?? '';
  if (text.includes('pause')) return 'paused';
  if (text.includes('close') || text.includes('filled')) return 'closed_filled';
  if (text.includes('delete') || text.includes('archived')) return 'deleted';
  if (text.includes('draft')) return 'draft';
  if (text.includes('publish') || text.includes('active') || text.includes('open')) return 'published';
  return publishedFallback ? 'published' : 'draft';
}

function coerceTargetHireCount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const rounded = Math.round(value);
    if (rounded < 1) return 1;
    if (rounded > 99) return 99;
    return rounded;
  }
  const text = asText(value);
  if (!text) return 1;
  const parsed = Number.parseInt(text, 10);
  if (!Number.isFinite(parsed)) return 1;
  if (parsed < 1) return 1;
  if (parsed > 99) return 99;
  return parsed;
}

function coerceBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  const text = asText(value)?.toLowerCase() ?? '';
  return text === 'true' || text === 'yes' || text === '1' || text === 'y';
}

function coerceTimestamp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Number(value);
  if (value instanceof Date) return value.getTime();
  const text = asText(value);
  if (!text) return null;
  const asNumber = Number(text);
  if (Number.isFinite(asNumber)) return asNumber;
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeInterviewStatus(value: unknown): string {
  const text = asText(value)?.toLowerCase() ?? '';
  if (!text) return 'not_scheduled';
  if (text.includes('progress')) return 'in_progress';
  if (text.includes('schedule') && !text.includes('not')) return 'scheduled';
  if (
    text.includes('complete') ||
    text.includes('completed') ||
    text.includes('hired') ||
    text.includes('reject')
  ) {
    return 'interview_complete';
  }
  if (text.includes('not') && text.includes('schedule')) return 'not_scheduled';
  return text;
}

function normalizeStageReviewerScores(
  value: unknown,
): Record<string, Record<string, number>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const rows = value as Record<string, unknown>;
  const normalized: Record<string, Record<string, number>> = {};
  for (const [phaseId, stageRaw] of Object.entries(rows)) {
    if (!phaseId.trim() || !stageRaw || typeof stageRaw !== 'object' || Array.isArray(stageRaw)) {
      continue;
    }
    const stage = stageRaw as Record<string, unknown>;
    const scores: Record<string, number> = {};
    for (const [reviewer, scoreRaw] of Object.entries(stage)) {
      const reviewerKey = reviewer.trim();
      if (!reviewerKey) continue;
      const parsed = Number(scoreRaw);
      if (!Number.isFinite(parsed)) continue;
      const bounded = Math.max(1, Math.min(10, Math.round(parsed)));
      scores[reviewerKey] = bounded;
    }
    if (Object.keys(scores).length > 0) {
      normalized[phaseId] = scores;
    }
  }
  return normalized;
}

function normalizeRemotePhases(rawValue: unknown, seed: string): JobApplicationPhase[] {
  if (!Array.isArray(rawValue)) return createDefaultJobApplicationPhases(seed);
  const mapped = rawValue
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;
      const raw = item as Record<string, unknown>;
      const name = asText(raw.name ?? raw.title ?? raw.stage);
      if (!name) return null;
      return {
        id: asText(raw.id) ?? `phase_${index + 1}_${seed}`,
        name,
        order:
          typeof raw.order === 'number' && Number.isFinite(raw.order)
            ? Number(raw.order)
            : index + 1,
        interviewer: asText(raw.interviewer ?? raw.owner ?? raw.assignee) ?? '',
        notes: asText(raw.notes) ?? '',
        scorecard:
          typeof raw.scorecard === 'number' && Number.isFinite(raw.scorecard)
            ? Number(raw.scorecard)
            : null,
      } satisfies JobApplicationPhase;
    })
    .filter((phase): phase is JobApplicationPhase => Boolean(phase))
    .sort((a, b) => a.order - b.order)
    .map((phase, index) => ({ ...phase, order: index + 1 }));

  return mapped.length ? mapped : createDefaultJobApplicationPhases(seed);
}

function normalizeRemoteJob(rawValue: unknown, index: number): CareerJob | null {
  const raw = (rawValue ?? {}) as Record<string, unknown>;
  const title = asText(raw.title ?? raw.name ?? raw.position);
  if (!title) return null;

  const slugSource = asText(raw.slug) ?? title;
  const slug = slugSource
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const id = asText(raw.id ?? raw.jobId ?? raw.uid ?? `${slug || 'job'}-${index}`) ?? `job-${index}`;
  const published = Boolean(raw.published ?? raw.isPublished ?? true);
  const listingStatus = coerceListingStatus(
    raw.listingStatus ?? raw.status ?? raw.lifecycleStatus,
    published,
  );
  const now = Date.now();

  return {
    id,
    slug: slug || `job-${index + 1}`,
    requisitionId: asText(raw.requisitionId ?? raw.requisitionNumber ?? raw.jobCode ?? raw.jobId) ?? `REQ-${index + 1}`,
    title,
    team: asText(raw.team ?? raw.department) ?? 'General',
    brainNodeId: asText(raw.brainNodeId ?? raw.teamNodeId ?? raw.nodeId) ?? undefined,
    location: asText(raw.location ?? raw.region) ?? 'Remote',
    workType: coerceWorkType(raw.workType ?? raw.employmentType ?? raw.type),
    summary: asText(raw.summary ?? raw.subtitle ?? raw.teaser) ?? title,
    description: asText(raw.description ?? raw.overview) ?? '',
    responsibilities: asTextArray(raw.responsibilities ?? raw.tasks),
    requirements: asTextArray(raw.requirements ?? raw.qualifications),
    published: listingStatus === 'published',
    listingStatus,
    applicationPhases: normalizeRemotePhases(
      raw.applicationPhases ?? raw.phases ?? raw.stages,
      id,
    ),
    hiringManager:
      asText(
        raw.hiringManager ??
          raw.hiringManagerEmail ??
          raw.hiringManagerName ??
          raw.manager,
      ) ?? '',
    targetHireCount: coerceTargetHireCount(
      raw.targetHireCount ?? raw.targetHires ?? raw.hiringCount,
    ),
    compensation: asText(raw.compensation ?? raw.salary ?? raw.payRange) ?? '',
    createdAt:
      typeof raw.createdAt === 'number' && Number.isFinite(raw.createdAt)
        ? Number(raw.createdAt)
        : now,
    updatedAt:
      typeof raw.updatedAt === 'number' && Number.isFinite(raw.updatedAt)
        ? Number(raw.updatedAt)
        : now,
    source: 'remote',
  };
}

function normalizeRemoteCandidate(
  rawValue: unknown,
  index: number,
): CandidateApplicationRecord | null {
  if (!rawValue || typeof rawValue !== 'object') return null;
  const raw = rawValue as Record<string, unknown>;
  const email = asText(raw.email ?? raw.applicantEmail);
  if (!email) return null;

  const firstName = asText(raw.firstName) ?? '';
  const lastName = asText(raw.lastName) ?? '';
  const combinedName = `${firstName} ${lastName}`.trim();
  const name = combinedName || email;
  const now = Date.now();
  const createdAt =
    typeof raw.createdAt === 'number' && Number.isFinite(raw.createdAt)
      ? Number(raw.createdAt)
      : now;
  const updatedAt =
    typeof raw.updatedAt === 'number' && Number.isFinite(raw.updatedAt)
      ? Number(raw.updatedAt)
      : createdAt;
  const jobId = asText(raw.jobId ?? raw.listingId) ?? '';
  const requisitionId = asText(raw.requisitionId ?? raw.requisitionNumber ?? raw.jobCode) ?? '';
  const resumeUrl = asText(raw.resumeDownloadUrl ?? raw.resumeUrl ?? raw.resumeLink) ?? '';
  const coverUrl =
    asText(raw.coverLetterDownloadUrl ?? raw.coverLetterUrl ?? raw.coverLetterLink) ?? '';
  const agentUserIdRaw = asText(raw.agentUserId ?? raw.convertedAgentUserId) ?? '';
  const agentUserId = agentUserIdRaw.trim() ? agentUserIdRaw.trim() : null;

  return {
    id: asText(raw.id ?? raw.uid ?? raw.candidateId ?? `candidate_remote_${index}`) ?? `candidate_remote_${index}`,
    role: 'candidate',
    firstName,
    lastName,
    name,
    email,
    contactPhone: asText(raw.contactPhone ?? raw.phone) ?? '',
    userType:
      asText(raw.userType ?? raw.customerType)?.toLowerCase() === 'business'
        ? 'business'
        : 'individual',
    businessName: asText(raw.businessName) ?? '',
    businessWebsite: asText(raw.businessWebsite) ?? '',
    businessAddress: asText(raw.businessAddress ?? raw.addressLine1 ?? raw.address) ?? '',
    candidateCity: asText(raw.candidateCity ?? raw.city) ?? '',
    candidateState: asText(raw.candidateState ?? raw.state ?? raw.regionCode) ?? '',
    candidatePostalCode:
      asText(raw.candidatePostalCode ?? raw.postalCode ?? raw.zip ?? raw.zipCode) ?? '',
    backgroundCheckAcknowledged: coerceBoolean(
      raw.backgroundCheckAcknowledged ?? raw.backgroundCheckConsent ?? raw.consentBackgroundCheck,
    ),
    pointOfContact: asText(raw.pointOfContact ?? raw.referredBy) ?? '',
    bio: asText(raw.bio ?? raw.notes) ?? '',
    linkedInUrl: asText(raw.linkedInUrl) ?? '',
    portfolioUrl: asText(raw.portfolioUrl ?? raw.portfolioWebsite) ?? '',
    coverLetterText: asText(raw.coverLetterText) ?? '',
    requisitionId,
    jobId,
    jobTitle: asText(raw.jobTitle ?? raw.title ?? raw.position) ?? '',
    resumeFileName: asText(raw.resumeFileName ?? raw.resumeName) ?? '',
    coverLetterFileName: asText(raw.coverLetterFileName ?? raw.coverLetterName) ?? '',
    resumeStoragePath: asText(raw.resumeStoragePath) ?? '',
    coverLetterStoragePath: asText(raw.coverLetterStoragePath) ?? '',
    resumeDownloadUrl: resumeUrl,
    coverLetterDownloadUrl: coverUrl,
    source: asText(raw.source)?.toLowerCase() === 'manual_admin' ? 'manual_admin' : 'job_board',
    status: normalizeCandidateApplicationStatus(asText(raw.status)),
    currentPhaseId: asText(raw.currentPhaseId ?? raw.phaseId) ?? '',
    reviewer: asText(raw.reviewer) ?? '',
    phaseNotes: asText(raw.phaseNotes ?? raw.notes) ?? '',
    phaseScorecard:
      typeof raw.phaseScorecard === 'number' && Number.isFinite(raw.phaseScorecard)
        ? Number(raw.phaseScorecard)
        : null,
    stageReviewerScores: normalizeStageReviewerScores(raw.stageReviewerScores),
    applicationUpdatedAt:
      typeof raw.applicationUpdatedAt === 'number' && Number.isFinite(raw.applicationUpdatedAt)
        ? Number(raw.applicationUpdatedAt)
        : updatedAt,
    createdAt,
    updatedAt,
    interviewStatus: normalizeInterviewStatus(
      raw.interviewStatus ?? raw.interview_state ?? raw.interviewLifecycleStatus,
    ),
    interviewScheduledAt: coerceTimestamp(raw.interviewScheduledAt),
    interviewCompletedAt: coerceTimestamp(raw.interviewCompletedAt),
    latestInterviewEventId:
      asText(raw.latestInterviewEventId ?? raw.interviewEventId) ?? null,
    interviewMeetingUrl: asText(raw.interviewMeetingUrl ?? raw.meetingUrl ?? raw.calendarUrl) ?? '',
    offerAccepted:
      coerceBoolean(raw.offerAccepted ?? raw.offerAcceptedByCandidate) || Boolean(agentUserId),
    agentUserId,
  };
}

function extractJobsPayload(payload: unknown): CareerJob[] {
  const value = payload as Record<string, unknown> | unknown[];
  const collection = Array.isArray(value)
    ? value
    : (value?.jobs as unknown[]) ??
      (value?.items as unknown[]) ??
      (value?.results as unknown[]) ??
      (value?.data as unknown[]) ??
      [];
  return collection
    .map((item, index) => normalizeRemoteJob(item, index))
    .filter((item): item is CareerJob => Boolean(item));
}

function extractCandidatesPayload(payload: unknown): CandidateApplicationRecord[] {
  const value = payload as Record<string, unknown> | unknown[];
  const collection = Array.isArray(value)
    ? value
    : (value?.candidates as unknown[]) ??
      (value?.items as unknown[]) ??
      (value?.results as unknown[]) ??
      (value?.data as unknown[]) ??
      [];
  return collection
    .map((item, index) => normalizeRemoteCandidate(item, index))
    .filter((item): item is CandidateApplicationRecord => Boolean(item));
}

async function fetchRemoteAdminJobs(api: ReturnType<typeof useApi>): Promise<CareerJob[]> {
  for (const endpoint of ADMIN_JOBS_LIST_ENDPOINTS) {
    try {
      const payload = await api.get(endpoint).then((response) => response.data);
      const jobs = extractJobsPayload(payload);
      if (jobs.length) return jobs;
    } catch {
      // try next endpoint
    }
  }
  return [];
}

async function fetchRemoteCandidates(
  api: ReturnType<typeof useApi>,
): Promise<CandidateApplicationRecord[]> {
  for (const endpoint of ADMIN_CANDIDATE_LIST_ENDPOINTS) {
    try {
      const payload = await api.get(endpoint).then((response) => response.data);
      const candidates = extractCandidatesPayload(payload);
      if (candidates.length) return candidates;
    } catch {
      // try next endpoint
    }
  }
  return [];
}

async function tryPatchJobRemote(
  api: ReturnType<typeof useApi>,
  jobId: string,
  payload: Record<string, unknown>,
): Promise<unknown> {
  let lastError: unknown = null;
  for (const base of ADMIN_JOB_UPDATE_ENDPOINTS) {
    try {
      const response = await api.patch(`${base}/${encodeURIComponent(jobId)}`, payload);
      return response.data;
    } catch (error) {
      lastError = error;
      // try next endpoint
    }
  }
  if (lastError instanceof Error) throw lastError;
  throw new Error('Unable to update job listing.');
}

async function tryDeleteJobRemote(
  api: ReturnType<typeof useApi>,
  jobId: string,
): Promise<unknown> {
  let lastError: unknown = null;
  for (const base of ADMIN_JOB_UPDATE_ENDPOINTS) {
    try {
      const response = await api.delete(`${base}/${encodeURIComponent(jobId)}`);
      return response.data;
    } catch (error) {
      lastError = error;
      // try next endpoint
    }
  }
  if (lastError instanceof Error) throw lastError;
  throw new Error('Unable to delete job listing.');
}

async function tryPatchCandidateRemote(
  api: ReturnType<typeof useApi>,
  candidateId: string,
  payload: Record<string, unknown>,
): Promise<unknown> {
  let lastError: unknown = null;
  for (const base of ADMIN_CANDIDATE_UPDATE_ENDPOINTS) {
    try {
      const response = await api.patch(
        `${base}/${encodeURIComponent(candidateId)}`,
        payload,
      );
      return response.data;
    } catch (error) {
      lastError = error;
      // try next endpoint
    }
  }
  if (lastError instanceof Error) throw lastError;
  throw new Error('Unable to update candidate application.');
}

export interface JobBoardSnapshot {
  jobs: CareerJob[];
  candidates: CandidateApplicationRecord[];
}

export function useJobBoardData() {
  const api = useApi();

  return useQuery<JobBoardSnapshot>({
    queryKey: ['job-board'],
    queryFn: async () => {
      const [remoteJobs, remoteCandidates] = await Promise.all([
        fetchRemoteAdminJobs(api),
        fetchRemoteCandidates(api),
      ]);
      const jobs = remoteJobs
        .map((job) => ({
          ...job,
          listingStatus: getCareerJobListingStatus(job),
          applicationPhases: getCareerJobApplicationPhases(job),
        }))
        .sort((a, b) => b.updatedAt - a.updatedAt);

      const candidates = remoteCandidates.sort((a, b) => b.updatedAt - a.updatedAt);
      return { jobs, candidates };
    },
    staleTime: 15 * 1000,
  });
}

export function useUpdateJobBoardListing() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      patch,
    }: {
      jobId: string;
      patch: Partial<
        Pick<
          CareerJob,
          | 'title'
          | 'requisitionId'
          | 'team'
          | 'brainNodeId'
          | 'location'
          | 'workType'
          | 'summary'
          | 'description'
          | 'responsibilities'
          | 'requirements'
          | 'published'
          | 'listingStatus'
          | 'applicationPhases'
          | 'hiringManager'
          | 'targetHireCount'
          | 'compensation'
        >
      >;
    }) => {
      await tryPatchJobRemote(api, jobId, patch as Record<string, unknown>);
      return { remoteSynced: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-board'] });
      queryClient.invalidateQueries({ queryKey: ['careers-jobs-public'] });
    },
  });
}

export function useSetJobBoardListingStatus() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      status,
    }: {
      jobId: string;
      status: JobListingStatus;
    }) => {
      const payload = {
        listingStatus: status,
        published: status === 'published',
      };
      await tryPatchJobRemote(api, jobId, payload);
      return { remoteSynced: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-board'] });
      queryClient.invalidateQueries({ queryKey: ['careers-jobs-public'] });
    },
  });
}

export function useDeleteJobBoardListing() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId }: { jobId: string }) => {
      await tryDeleteJobRemote(api, jobId);
      return { remoteDeleted: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-board'] });
      queryClient.invalidateQueries({ queryKey: ['careers-jobs-public'] });
    },
  });
}

export function useUpdateJobBoardCandidateLifecycle() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      candidateId,
      status,
      phaseId,
      reviewer,
      notes,
      scorecard,
      stageReviewerScores,
      interviewMeetingUrl,
      offerAccepted,
    }: {
      candidateId: string;
      status: CandidateApplicationStatus;
      phaseId: string;
      reviewer: string;
      notes: string;
      scorecard: number | null;
      stageReviewerScores?: Record<string, Record<string, number>>;
      interviewMeetingUrl?: string;
      offerAccepted?: boolean;
    }) => {
      await tryPatchCandidateRemote(api, candidateId, {
        status: normalizeCandidateApplicationStatus(status),
        phaseId,
        reviewer,
        phaseNotes: notes,
        phaseScorecard: scorecard,
        stageReviewerScores,
        interviewMeetingUrl,
        offerAccepted,
      });
      return { remoteSynced: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-board'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

export function useSaveJobBoardListing() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ job }: { job: CareerJob }) => {
      await tryPatchJobRemote(api, job.id, {
        ...job,
        listingStatus: getCareerJobListingStatus(job),
        applicationPhases: getCareerJobApplicationPhases(job),
      });
      return { remoteSynced: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-board'] });
      queryClient.invalidateQueries({ queryKey: ['careers-jobs-public'] });
    },
  });
}
